// User Access gate: collects the profile and password, creates the account
// for a new email, and signs a returning user in.
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import './PasswordGate.css';
import '../auth/LoginPage.css';
import '../auth/TermsAcceptance.css';
import { saveTesterUnlock } from '../../helpers/testerAccess';
import { proceedAccessGateUser, registerUser, clearAuth } from '../../store/actions/authActions';
import GoogleSignInButton from '../auth/GoogleSignInButton';
import TermsAcceptance from '../auth/TermsAcceptance';
import { goToVerifyEmailPending } from '../../helpers/authNavigation';
import { clearSubscription } from '../../store/actions/subscriptionActions';
import * as authService from '../../services/authService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const focusRowInput = (e) => {
  const row = e.currentTarget;
  const input = row && row.querySelector('input');
  if (input && !input.disabled) {
    input.focus();
  }
};

const PasswordGate = ({
  bootstrapped,
  loading,
  clearAuthAction,
  clearSubscriptionAction,
  proceedAccessGateAction,
  registerUserAction,
}) => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedOrg = organization.trim();
    const trimmedCountry = country.trim();

    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!trimmedOrg) {
      setError('Please enter your affiliation.');
      return;
    }
    if (!trimmedCountry) {
      setError('Please enter your country.');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    // Users choose their own password here: a new account is created with it
    // directly, and a returning user is authenticated against it by the API,
    // which is what actually protects the account.
    if (!password) {
      setError('Please enter a password.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      try {
        await authService.logout();
      } catch {
        // continue
      }
      authService.clearLocalSession();
      clearAuthAction();
      clearSubscriptionAction();

      const profile = {
        name: trimmedName,
        email: trimmedEmail.toLowerCase(),
        organization: trimmedOrg,
        country: trimmedCountry,
      };
      saveTesterUnlock(profile);

      const data = await proceedAccessGateAction({
        email: profile.email,
        name: profile.name,
        organization: profile.organization,
        country: profile.country,
        password,
      });

      if (data.isNewUser) {
        if (!termsAccepted) {
          setError('Please accept the Terms & Conditions below to create your account.');
          return;
        }
        await registerUserAction({
          email: profile.email,
          password,
          name: profile.name,
          acceptTerms: termsAccepted,
        });

        goToVerifyEmailPending(history, profile.email);
        return;
      }

      if (data.requiresEmailVerification) {
        goToVerifyEmailPending(history, profile.email);
        return;
      }

      history.push(data.redirectTo || '/subscription');
    } catch (err) {
      setError(err.message || 'Could not complete User Access. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!bootstrapped || loading) {
    return (
      <div className="xk-gate xk-gate--loading">
        <div className="text-center">
          <div className="spinner-border text-success" role="status" />
          <p className="mt-3 text-muted mb-0">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="xk-gate">
      <div className="xk-gate__card">
        <img
          src={`${process.env.PUBLIC_URL || ''}/XLog3D Logo_Red.png`}
          alt="XLog3D"
          className="xk-gate__logo"
        />

        <div className="xk-gate__note">
          <div className="xk-gate__note-title">Attention Please!</div>
          <p>Please use computer to run the App</p>
        </div>

        <h2 className="xk-gate__title">User Access</h2>

        <form className="xk-gate__form" onSubmit={handleSubmit} autoComplete="off" noValidate>
          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="gate-name">Name</label>
            <input
              id="gate-name"
              className="xk-gate__row-input"
              type="text"
              value={name}
              disabled={busy}
              autoFocus
              autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError('');
                if (info) setInfo('');
              }}
            />
          </div>

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="gate-affiliation">Affiliation</label>
            <input
              id="gate-affiliation"
              className="xk-gate__row-input"
              type="text"
              value={organization}
              disabled={busy}
              autoComplete="off"
              onChange={(e) => {
                setOrganization(e.target.value);
                if (error) setError('');
                if (info) setInfo('');
              }}
            />
          </div>

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="gate-country">Country</label>
            <input
              id="gate-country"
              className="xk-gate__row-input"
              type="text"
              value={country}
              disabled={busy}
              autoComplete="off"
              onChange={(e) => {
                setCountry(e.target.value);
                if (error) setError('');
                if (info) setInfo('');
              }}
            />
          </div>

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="gate-email">Email</label>
            <input
              id="gate-email"
              className="xk-gate__row-input"
              type="text"
              inputMode="email"
              value={email}
              disabled={busy}
              autoComplete="off"
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError('');
                if (info) setInfo('');
              }}
            />
          </div>

          <div className="xk-gate__row" onMouseDown={focusRowInput}>
            <label className="xk-gate__row-label" htmlFor="gate-access-code">Password</label>
            <input
              id="gate-access-code"
              className="xk-gate__row-input xk-gate__row-input--secret"
              // A real password field now that it holds the user's own
              // password: masked by the browser, and offered to password
              // managers instead of being suppressed.
              type="password"
              value={password}
              disabled={busy}
              autoComplete="current-password"
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
                if (info) setInfo('');
              }}
            />
          </div>

          <TermsAcceptance
            id="gate-terms"
            checked={termsAccepted}
            disabled={busy}
            onChange={(value) => {
              setTermsAccepted(value);
              if (error) setError('');
              if (info) setInfo('');
            }}
          />

          {error && <div className="xk-gate__error">{error}</div>}
          {info && <div className="xk-gate__info">{info}</div>}

          <button type="submit" className="xk-gate__submit" disabled={busy}>
            {busy ? 'Please wait...' : 'PROCEED'}
          </button>

          <div className="xk-gate__divider"><span>or</span></div>

          {termsAccepted ? (
            <GoogleSignInButton
              organization={organization}
              country={country}
              acceptTerms={termsAccepted}
              onError={setError}
            />
          ) : (
            <p className="xk-terms-hint xk-terms-hint--center">
              Accept the Terms &amp; Conditions above to sign up with Google.
            </p>
          )}
        </form>

        <div className="xk-login-links">
          <button
            type="button"
            className="xk-login-link"
            disabled={busy}
            onClick={() => history.push('/login')}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

PasswordGate.propTypes = {
  bootstrapped: PropTypes.bool,
  loading: PropTypes.bool,
  clearAuthAction: PropTypes.func.isRequired,
  clearSubscriptionAction: PropTypes.func.isRequired,
  proceedAccessGateAction: PropTypes.func.isRequired,
  registerUserAction: PropTypes.func.isRequired,
};

PasswordGate.defaultProps = {
  bootstrapped: false,
  loading: false,
};

const mapStateToProps = (state) => ({
  bootstrapped: state.auth.bootstrapped,
  loading: state.auth.loading,
});

const mapDispatchToProps = {
  clearAuthAction: clearAuth,
  clearSubscriptionAction: clearSubscription,
  proceedAccessGateAction: proceedAccessGateUser,
  registerUserAction: registerUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(PasswordGate);
